import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type HTMLAttributes,
  type ReactNode,
  type RefObject,
} from "react";
import { cx } from "../../cx";
import { useIsMobile } from "../../useIsMobile";
import { BottomSheet } from "../BottomSheet/BottomSheet";

export interface PopoverTriggerProps {
  readonly ref: RefObject<HTMLButtonElement | null>;
  readonly onClick: () => void;
  readonly "aria-haspopup": "dialog" | "menu";
  readonly "aria-expanded": boolean;
  readonly "aria-controls": string | undefined;
}

export interface PopoverProps {
  /** Render-prop: spread the props on a `<button>` (IconButton/Button). */
  readonly trigger: (props: PopoverTriggerProps) => ReactNode;
  /** Accessible name of the panel (also the sheet title on phones). */
  readonly label: string;
  readonly children: ReactNode | ((close: () => void) => ReactNode);
  /** Inline edge the panel aligns to; logical, so it flips with RTL. */
  readonly placement?: "start" | "end" | undefined;
  readonly role?: "dialog" | "menu" | undefined;
  readonly open?: boolean | undefined;
  readonly onOpenChange?: ((open: boolean) => void) | undefined;
  /** Show as a bottom sheet below this breakpoint (default `md`; `never` keeps the popover). */
  readonly sheetBelow?: "md" | "never" | undefined;
  readonly className?: string | undefined;
  readonly panelClassName?: string | undefined;
  readonly testId?: string | undefined;
  readonly panelProps?: HTMLAttributes<HTMLDivElement>;
}

/**
 * Anchored popover (outside-click, Esc, focus return). Below `md` it renders the same content in a
 * `BottomSheet` (G14). Controlled via `open`/`onOpenChange`, or uncontrolled.
 */
export function Popover({
  trigger,
  label,
  children,
  placement = "end",
  role = "dialog",
  open: openProp,
  onOpenChange,
  sheetBelow = "md",
  className,
  panelClassName,
  testId,
  panelProps,
}: PopoverProps) {
  const [inner, setInner] = useState(false);
  const open = openProp ?? inner;
  const setOpen = useCallback(
    (v: boolean) => {
      if (openProp === undefined) setInner(v);
      onOpenChange?.(v);
    },
    [openProp, onOpenChange],
  );
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const id = useId();
  const mobile = useIsMobile();
  const sheet = sheetBelow === "md" && mobile;

  const close = useCallback(() => setOpen(false), [setOpen]);
  const [pos, setPos] = useState<CSSProperties>({});

  // The panel is `position: fixed`, placed from the trigger's rect, so a table/overflow wrapper can
  // never clip it (same approach as RowMenu); it flips above the trigger when there is no room.
  useLayoutEffect(() => {
    if (!open || sheet) return;
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const rtl = getComputedStyle(el).direction === "rtl";
    const below = window.innerHeight - rect.bottom;
    const flip = below < 280 && rect.top > below;
    const atEnd = placement === "end";
    // physical anchor edge of the trigger: end = right in LTR, left in RTL
    const anchorRight = atEnd !== rtl;
    setPos({
      ...(anchorRight
        ? { right: Math.max(8, window.innerWidth - rect.right) }
        : { left: Math.max(8, rect.left) }),
      ...(flip ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
    });
  }, [open, sheet, placement]);

  useEffect(() => {
    if (!open || sheet) return;
    const onDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    const onScroll = (e: Event) => {
      if (panelRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", close);
    };
  }, [open, sheet, setOpen, close]);

  const body = typeof children === "function" ? children(close) : children;

  return (
    <div ref={rootRef} className={cx("relative inline-block", className)} data-testid={testId}>
      {trigger({
        ref: triggerRef,
        onClick: () => setOpen(!open),
        "aria-haspopup": role,
        "aria-expanded": open,
        "aria-controls": open ? id : undefined,
      })}
      {open && sheet && (
        <BottomSheet open title={label} onClose={close}>
          <div id={id} role={role === "menu" ? "menu" : undefined} aria-label={label}>
            {body}
          </div>
        </BottomSheet>
      )}
      {open && !sheet && (
        <div
          {...panelProps}
          ref={panelRef}
          id={id}
          role={role}
          aria-label={label}
          style={pos}
          className={cx(
            "fixed z-40 min-w-48 max-w-[min(22rem,90vw)] rounded-xl border border-line bg-surface p-3 text-start text-sm text-fg shadow-md",
            panelClassName,
          )}
        >
          {body}
        </div>
      )}
    </div>
  );
}
